/**
 * C02 (HTTP/WS API) + C01 §3/§6 的 TypeScript 對應型別。
 *
 * 這份檔案是「手抄的契約」：每個型別都標了它來自契約的哪一節。
 * 契約是 FROZEN 的 —— 若這裡跟契約對不起來，要改的是這裡，不是契約。
 * 契約沒寫死的形狀（見下方 `@contract-gap` 註記）一律用寬鬆型別 + optional，
 * 並在 docs/ai/handoffs/ 留下待確認事項。
 */

// ---------------------------------------------------------------------------
// 基本型別（C01 §1）
// ---------------------------------------------------------------------------

export type Seat = number;
export type ActionKey = string;

/** C01 §5：RPS 的動作字彙。ActionId 0/1/2 是引擎內部編碼，前端一律用 ActionKey。 */
export const RPS_ACTIONS = ['rock', 'paper', 'scissors'] as const;
export type RpsAction = (typeof RPS_ACTIONS)[number];

export function isRpsAction(x: string): x is RpsAction {
  return (RPS_ACTIONS as readonly string[]).includes(x);
}

// ---------------------------------------------------------------------------
// 錯誤（C02 §0）
// ---------------------------------------------------------------------------

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    detail?: Record<string, unknown>;
  };
}

// ---------------------------------------------------------------------------
// 公開目錄（C02 §2）
// ---------------------------------------------------------------------------

export interface VariantSummary {
  variant_key: string;
  display_name: string;
  published: boolean;
  config: RpsVariantConfig & Record<string, unknown>;
  online_players?: number;
}

/** C01 §5 的 variant_schema。全部 optional：後端可能只回部分欄位。 */
export interface RpsVariantConfig {
  rounds_to_win?: number;
  max_rounds?: number;
  draw_policy?: 'replay' | 'count_as_round';
  move_timeout_ms?: number;
  on_timeout?: 'random' | 'forfeit_round' | 'forfeit_match';
  reveal_history?: 'full' | 'own_only' | 'none';
  allow_rematch?: boolean;
  /** C03 §2：guest 只能玩 allow_anonymous=true 的變體。 */
  allow_anonymous?: boolean;
}

export interface GameSummary {
  game_key: string;
  display_name: string;
  information: 'perfect' | 'imperfect';
  move_timing: 'simultaneous' | 'sequential';
  lobby_url: string;
  variants: VariantSummary[];
}

export interface GamesResponse {
  items: GameSummary[];
}

export interface GameDetail extends GameSummary {
  variant_schema?: Record<string, unknown>;
}

export interface LeaderboardRow {
  rank: number;
  display_name: string;
  wins: number;
  losses: number;
  draws: number;
  rating?: number | null;
}

export interface Paged<T> {
  items: T[];
  next_cursor: string | null;
}

// ---------------------------------------------------------------------------
// 認證（C02 §3 / C03 §2）
// ---------------------------------------------------------------------------

export interface GuestTokenResponse {
  guest_token: string;
  expires_at: string;
  subject_id: string;
  display_name: string;
}

// ---------------------------------------------------------------------------
// 大廳與媒合（C02 §4）
// ---------------------------------------------------------------------------

export interface LobbyVariantStatus {
  variant_key: string;
  queued: number;
  open_tables: number;
}

export interface LobbyResponse {
  variants: LobbyVariantStatus[];
  my_active_match: string | null;
  online_count: number;
}

export type MatchmakingMode = 'random' | 'direct' | 'invite' | 'vs_ai';

export interface QueueRequest {
  variant_key: string;
  mode: MatchmakingMode;
  opponent_subject_id?: string | null;
  invite_code?: string | null;
  ai_profile?: string | null;
  rating_band?: string | null;
}

export interface QueueTicketCreated {
  ticket_id: string;
  status: TicketStatus;
  estimated_wait_ms?: number;
}

export type TicketStatus = 'queued' | 'matched' | 'expired' | 'cancelled';

export interface QueueTicketStatus {
  status: TicketStatus;
  match_id?: string | null;
}

export interface InviteResponse {
  invite_code: string;
  join_url: string;
  expires_at: string;
  variant_key: string;
}

export interface JoinResponse {
  match_id: string;
}

// ---------------------------------------------------------------------------
// 對局（C02 §5、C01 §3）
// ---------------------------------------------------------------------------

/**
 * 座位上的參與者種類 —— `GET /matches/{id}` 的 `seats[].kind`（C02 §5）。
 *
 * ★ 這一組值的權威是**資料庫 CHECK 約束**，不是這個檔案：
 * `match_seat_kind_ck: participant_kind IN ('human','guest','ai')`（C04 §1.2）。
 * 後端 `api/match_service.py` 直接送 `s.participant_kind`，中間沒有任何映射。
 *
 * ★ 為什麼是「`as const` 陣列 + 由陣列導出型別」而不是手寫聯集：
 * 與 {@link EVENT_TYPES} 完全相同的理由 —— 純型別的聯集編譯後消失，
 * 沒有任何測試讀得到，於是它與後端分岔時什麼都不會變紅。
 * 比對這個清單的測試是 `src/test/response-contract.test.ts` 的
 * 「★ 座位 kind 的值域在六個宣告點必須一致」。
 *
 * ⚠️ 維持「一行一個字串字面量」，跨檔比對的解析器靠這個形狀。
 *
 * ## 修復前是什麼樣子（2026-08-16，第五個接縫）
 *
 *     export type SeatKind = 'guest' | 'player' | 'ai';
 *
 * * **少了 `human`**：`auth/principal.py` 的
 *   `return "guest" if self.is_guest else "human"` 表示**每一個 Keycloak
 *   登入玩家**的座位 kind 都是 `human`，而型別裡沒有這個值。
 * * **多了 `player`**：整個 repo 沒有任何地方把它寫進 `participant_kind`。
 *   它是 realm 名稱 `elab-gaming-players`（`Principal.is_player`）的殘影。
 *
 * 管理台 `apps/gaming-game-admin-frontend-js/src/api/types.ts` 的 `MatchSeat.kind`
 * 一直都是對的（`'human' | 'guest' | 'ai'`），所以錯的是這一邊。
 *
 * 這與 `winner`/`winner_seat`、`move_timeout_ms`/`deadline_at`、
 * publish 早於 subscribe、`seats` 形狀是**同一種失效模式**：
 * 兩邊各自都對、兩邊測試都綠、錯的是接縫。
 */
export const SEAT_KINDS = [
  'human',
  'guest',
  'ai',
] as const;

/** 與改動前那個手寫聯集**完全相同**的型別。 */
export type SeatKind = (typeof SEAT_KINDS)[number];

export interface SeatInfo {
  seat: Seat;
  display_name: string;
  /**
   * ★★ Unknown is `null` — 「讀不懂」與「真的是訪客」**不得**編碼成同一個值。
   *
   * `null` 的意思是**這個座位的 kind 我們不知道**，可能是：
   *   * `match_started.payload.seats` 送的是**座位號**（`[0, 1]`），本來就沒帶 kind；
   *   * 後端送了一個不在 {@link SEAT_KINDS} 裡的值（值域擴充了、前端還沒跟上）。
   *
   * **它不是「訪客」。** 舊實作把讀不懂的 kind 一律改寫成 `'guest'`，
   * 於是每一個 Keycloak 登入玩家（後端送 `"human"`）在 `state.seats` 裡都變成訪客 ——
   * 那是**資料損毀**，而且查起來會查到後端去（後端送的是對的）。
   *
   * 這與 C02 §6.1 禁止的模式逐字相同：「把『我讀不到』和『真的是 X』編碼成同一個值，
   * 前者就會安靜地偽裝成後者」。所以讀不懂時：**標成 null ＋ 記 `PROTOCOL_MISMATCH`**，
   * 原字串留在 {@link SeatInfo.kind_raw}。
   *
   * ⚠️ UI 規則：`kind === null` 時**不得**顯示任何身分徽章（訪客／註冊玩家／AI）。
   *    沒有資訊就是沒有資訊，猜一個看起來合理的比空白危險。
   */
  kind: SeatKind | null;
  /**
   * 線路上原封不動的 `kind` 字串。**只有在 `kind === null` 且後端真的送了字串時才存在。**
   *
   * 存在的理由是「不得靜默改寫」的另一半：`kind` 標成 unknown 是為了不說謊，
   * 這個欄位是為了不遺失證據 —— 值域擴充（`docs/architecture/02-shared-db-compatibility.md`
   * 已預告 `bot` / `observer`）時，診斷的人要看得到後端到底送了什麼。
   */
  kind_raw?: string;
  connected: boolean;
}

/**
 * C01 §3 Observation。
 * ★ 這是不完全訊息的邊界：`private` 只含「我這個座位」的私有資料。
 *   前端永遠不應該期待這裡出現對手的 pending 動作 —— 若出現了，那是後端的資訊洩漏 bug。
 */
export interface Observation {
  seat?: Seat;
  public: Record<string, unknown>;
  private: Record<string, unknown>;
  legal_actions: ActionKey[];
  /** 同時出手制：回傳「尚未出手」的座位集合（C01 §2）。 */
  current_players: Seat[];
  round_no?: number;
  terminal: boolean;
  returns?: Record<string, number> | null;
}

/**
 * 已結算回合。`winner === null` 視為平手。
 * @contract-gap C02 §5 的範例只示範了 `"winner": 0`，沒有明定平手怎麼表示。
 */
export interface HistoryEntry {
  round_no: number;
  actions: Record<string, ActionKey>;
  winner: Seat | null;
}

export interface Fairness {
  /** 形如 `sha256:<hex>`；對局進行中就會公布（C01 §4.2 步驟 1）。 */
  server_seed_commit: string;
  /** 對局結束前恆為 null（C02 §5）。 */
  server_seed: string | null;
  /** 對局結束後才有（C01 §4.2 步驟 5）。 */
  transcript_hash?: string | null;
}

/**
 * 後端 `gaming.match.status` 的值域（`persistence/models.py` + `match_service.py`）。
 *
 * ★ 舊版宣告的是 `'waiting' | 'in_progress' | 'ended' | 'voided'`：
 *   * `'ended'` **後端從來不會送** —— 結束的對局是 `'completed'`。
 *     `machine.ts` 裡的 `m.status === 'ended'` 因此是一段對真後端永遠為 false 的死碼
 *     （幸好還有 `observation.terminal` 兜著，所以沒有變成 bug，但那是運氣不是設計）。
 *   * 少了 `'ready'`（配對完成、還沒開局）與 `'abandoned'`（雙方都沒連上）——
 *     `'abandoned'` 會被當成「還在進行中」，玩家會一直等一場不會開始的對局。
 */
export type MatchStatus =
  | 'waiting'
  | 'ready'
  | 'in_progress'
  | 'completed'
  | 'abandoned'
  | 'voided';

/** 這些狀態代表對局已經結束，不會再有動作（`view_for()` 的 status）。 */
export function isTerminalStatus(status: MatchStatus | null): boolean {
  return status === 'completed' || status === 'abandoned' || status === 'voided';
}

export interface MatchView {
  match_id: string;
  variant_key: string;
  status: MatchStatus;
  my_seat: Seat;
  seats: SeatInfo[];
  round_no: number;
  /** key 是座位號的字串形式（JSON object key 只能是字串）。 */
  scores: Record<string, number>;
  observation: Observation;
  history: HistoryEntry[];
  fairness: Fairness;
  deadline_at: string | null;
}

export interface SubmitActionRequest {
  round_no: number;
  action: ActionKey;
}

/**
 * C02 §5：`waiting_for` 是「還沒出手的座位」。
 * ★ 本前端**刻意不使用**這個欄位來改變畫面 —— 見 docs/design/ui/01-player-experience.md §4.3。
 *   理由：讓兩位玩家看到的等待畫面完全一樣，UI 就不會變成一條「對手已出手」的時序旁通道。
 */
export interface SubmitActionResponse {
  accepted: boolean;
  round_no: number;
  waiting_for?: Seat[];
}

/**
 * `/replay` 的一個回合 —— 來自引擎的 `public_rounds()`（`core/engine.py:292-318`）。
 *
 * ★ **不是 `HistoryEntry`。** 沒有 `winner` 這個欄位：
 *   * `outcome` 是 transcript 的正規字串（`"draw"` 或 `"win:<seat>"`）；
 *   * 勝方在 `detail.winner`。
 *   舊版把 `rounds` 宣告成 `HistoryEntry[]`，於是回放頁讀 `round.winner`（永遠 undefined），
 *   `outcomeFor(seat, undefined)` 一律回 `'lose'` ——
 *   **每一場回放的每一回合都被畫成「敗」**，包括你贏的那些。
 *   型別對了，但畫面在說謊；這種 bug 不會有人回報成「型別錯誤」。
 */
export interface ReplayRound {
  round_no: number;
  actions: Record<string, ActionKey>;
  /** `"draw"` | `"win:<seat>"` —— 就是被雜湊進 transcript_hash 的那個字串。 */
  outcome: string;
  detail: {
    winner: Seat | null;
    draw: boolean;
    replayed: boolean;
    timeout_seats: Seat[];
  };
}

/**
 * `GET /matches/{id}/replay`（C02 §5）。
 *
 * 形狀 = 引擎的 `public_replay()` ＋ `match_service.replay()` 補上的
 * `seats` / `scores` / `ended_at`（`api/match_service.py:485-511`）。
 * WS-05a 當時標的 `@contract-gap` 已經由後端的實作回答了 —— 這裡是**實測的**形狀，
 * 不再是假設（樣本見 `src/test/response-fixtures.json`）。
 */
export interface ReplayResponse {
  match_id: string;
  game_key: string;
  variant_key: string;
  config: RpsVariantConfig & Record<string, unknown>;
  rounds: ReplayRound[];
  /** 座位 → 報酬（+1 / -1 / 0）。 */
  returns: Record<string, number>;
  seats: SeatInfo[];
  scores: Record<string, number>;
  /** **裸 hex 位元組**（C01 §4.2.1）；驗證時要先 `fromHex()`。 */
  server_seed: string;
  server_seed_commit: string;
  transcript_hash: string;
  ended_at?: string | null;
  /** transcript 的正規化編碼版本，例如 `"engine-canonical-v1"`。 */
  transcript_encoding?: string;
}

/** `ReplayRound` → `HistoryEntry`：回放頁與 `HistoryTable` 共用同一個顯示型別。 */
export function replayRoundToHistory(round: ReplayRound): HistoryEntry {
  return {
    round_no: round.round_no,
    actions: round.actions,
    // ★ `detail.winner`，不是 `winner`（後者不存在）。平手時後端給 null。
    winner: round.detail?.winner ?? null,
  };
}

/**
 * `POST /matches/{id}/rematch` 的回應。
 *
 * ★ 後端回的是 `{status, match_id}`（`api/routes_matches.py:191` 與 `:226`）：
 *   * `"waiting_for_opponent"` → `match_id` 是**原本那場**；
 *   * `"matched"` → `match_id` 是**新開的那場**，前端必須把玩家帶過去。
 *   舊型別是 `{requested, both_requested?, match_id?}`，一個欄位都對不上；
 *   而 `useMatch.rematch()` 又把回傳值丟掉了 ——
 *   結果是**雙方都按了再戰，卻沒有人會被帶進新的對局**。
 */
export interface RematchResponse {
  status: 'waiting_for_opponent' | 'matched';
  match_id: string;
}

// ---------------------------------------------------------------------------
// WebSocket（C02 §6、C01 §6）
// ---------------------------------------------------------------------------

/**
 * 引擎產生的事件名稱 —— 逐字對應 C01 §6（**已凍結**）與後端 `core/types.py::EventType`。
 *
 * 這一份是 `packages/game-protocol/src/protocol.ts::ENGINE_EVENT_TYPES` 的副本。
 */
export const ENGINE_EVENT_TYPES = [
  'match_created',
  'seat_joined',
  'seat_left',
  'match_started',
  'round_started',
  'action_received',
  'round_resolved',
  'match_ended',
  'match_voided',
  'admin_action',
] as const;

/**
 * **不是**引擎事件 —— 由服務層用 `Hub.publish_raw()` 直接發，
 * 因此後端 `core/types.py` 的 `EventType` 裡（依 C01 §6 的定義）沒有它們，前端卻**收得到**：
 *
 * | 事件 | 誰發的 | 契約 |
 * | :-- | :-- | :-- |
 * | `rematch_ready` | `api/routes_matches.py`（送到**舊**對局的頻道） | C02 §5 v1.2 |
 * | `lobby_updated` | `lobby/matchmaker.py`（大廳頻道） | C02 §4 |
 *
 * ★ 少列它們不是「型別比較保守」，是**型別在說謊** ——
 * 照著這份聯集寫 `switch` 的人會以為那兩個事件不存在，
 * 而 `rematch_ready` 缺席時的症狀是「先按再戰的人永遠停在等待畫面」（FINDINGS F-3）。
 */
export const SERVICE_EVENT_TYPES = ['rematch_ready', 'lobby_updated'] as const;

/**
 * 客戶端可能在 WS 上收到的**全部**事件名稱（C02 §6）= 引擎事件 ∪ 服務層事件。
 *
 * ★ 為什麼是「`as const` 陣列 + 由陣列導出型別」，而不是手寫的字面量聯集：
 *
 * 這份清單在 repo 裡有**三份副本**（本檔、`packages/game-protocol/src/protocol.ts`、
 * 後端 `core/types.py`），而純型別的 `type X = 'a' | 'b'` 編譯後完全消失，
 * **沒有任何測試讀得到它**，所以三份分岔時不會有東西變紅。
 * 那正是 F-4（`winner_seat` vs `winner`）與 A-1（`move_timeout_ms` vs `deadline_at`）的形狀。
 *
 * 改成陣列之後型別完全等價（沒有放寬也沒有收窄），但清單在執行期讀得到，
 * 於是 `src/test/response-contract.test.ts` 可以真的去比對三份副本。
 */
export const EVENT_TYPES = [...ENGINE_EVENT_TYPES, ...SERVICE_EVENT_TYPES] as const;

/** 與改動前那個手寫聯集**完全相同**的型別。 */
export type EventType = (typeof EVENT_TYPES)[number];

export interface ServerEventMessage {
  op: 'event';
  event_type: EventType;
  match_id: string;
  round_no?: number | null;
  seat?: Seat | null;
  payload: Record<string, unknown>;
  occurred_at?: string;
}

export interface ServerErrorMessage {
  op: 'error';
  code: string;
  message: string;
}

export interface ServerPongMessage {
  op: 'pong';
}

/** C02 §6.2：伺服器每 20s 送 ping，60s 收不到 pong 視為斷線 → 我們必須回 pong。 */
export interface ServerPingMessage {
  op: 'ping';
}

/**
 * `{"op":"resync"}` 的回覆。
 * @contract-gap C02 §6.3 只說「伺服器回完整 Observation」，沒有給 envelope 形狀。
 *   本前端同時接受這個形狀**並且**在 resync 後另外打一次 `GET /matches/{id}`，
 *   兩者先到先用（REST 那條路是契約明文保證的，所以永遠有退路）。
 */
export interface ServerSyncMessage {
  op: 'sync';
  match_id: string;
  match: MatchView;
}

export type WsServerMessage =
  | ServerEventMessage
  | ServerErrorMessage
  | ServerPongMessage
  | ServerPingMessage
  | ServerSyncMessage;

export type WsClientMessage =
  | { op: 'subscribe'; channel: string }
  | { op: 'action'; match_id: string; round_no: number; action: ActionKey; idempotency_key: string }
  | { op: 'resync'; match_id: string }
  | { op: 'ping' }
  | { op: 'pong' };

// ---------------------------------------------------------------------------
// 事件 payload（C01 §6 的 payload 是 dict，這裡給預期形狀 + 防禦性解析）
// ---------------------------------------------------------------------------
//
// ★★ 這一區是**文件**，`machine.ts` 不會 import 它們去解析 payload
//    （那裡一律走防禦性 reader，因為線路上的東西不保證是這個形狀）。
//    正因為它只是文件，寫錯的代價才特別隱蔽：不會有任何測試變紅，
//    但**下一個照著它寫程式的人會重現同一個缺陷**。
//    FINDINGS F-4 與 A-1 都是這樣來的，所以下面每一個欄位都標了取樣來源。
//
//    取樣來源：`src/test/event-fixtures.json`（由 `tools/capture-event-fixtures.py`
//    直接從後端擷取的 golden file），**不是**從契約文字或既有前端程式碼抄的。

/**
 * `round_started`（C02 §6.2 v1.3）。
 *
 * ★ `deadline_at` 是**絕對時刻**，而且契約說它是**強制**欄位。
 *   這裡仍然標成 optional，因為型別要描述的是「線路上可能出現什麼」，
 *   而後端服務層補上它之前，線路上真的沒有它（golden file 可以作證）。
 *   **標成必填會讓型別看起來比現實樂觀**，於是照著寫的人就不會處理缺席那條路徑 ——
 *   而契約明文要求客戶端在缺席時記錯誤並退化推導（見 `machine.ts` 的 `resolveRoundDeadline`）。
 *
 * ★ `move_timeout_ms` 舊版**沒有列**，這是 A-1 的型別層共犯：
 *   後端從第一天就在送它，而它正是退化推導唯一的材料。
 *   型別裡沒有它，等於告訴下一個人「這個欄位不存在」。
 */
export interface RoundStartedPayload {
  /** ISO-8601 UTC 絕對時刻。C02 §6.2 強制；缺席 = 協定不相容（但仍須退化推導）。 */
  deadline_at?: string;
  /** 本回合的時長（毫秒）。診斷／退化推導用，**不可**當成期限直接倒數。 */
  move_timeout_ms?: number;
  legal_actions?: ActionKey[];
  round_no?: number;
  scores?: Record<string, number>;
  server_seed_commit?: string;
}

/**
 * `round_resolved` —— **回合層級**，勝者欄位叫 `winner`（C02 §6.1）。
 * 平手是 `winner: null` **且** `draw: true`（C02 §5 已釘死）。
 */
export interface RoundResolvedPayload {
  actions?: Record<string, ActionKey>;
  winner?: Seat | null;
  draw?: boolean;
  replayed?: boolean;
  scores?: Record<string, number>;
  round_no?: number;
  rounds_played?: number;
  decisive_rounds?: number;
  timeout_seats?: Seat[];
  reveal_history?: 'full' | 'own_only' | 'none';
}

/**
 * `match_ended` —— **對局層級**，勝者欄位叫 `winner_seat`（C02 §6.1）。
 *
 * ★★ 舊版宣告的是 `winner?: Seat | null`，那是 **F-4 的錯名字**：
 *   後端從來沒送過 `match_ended.winner`，整條對局層級的資料鏈
 *   （DB 的 `match.winner_seat`、`state.public["winner_seat"]`、
 *   管理端 `force-end` 的 body）都叫 `winner_seat`。
 *   照著舊型別寫的 `readWinner()` 對每一場對局都讀到 `undefined`，
 *   於是**每一場的結束標題都顯示「和局」**，而 84 個前端測試全綠。
 *   C02 §6.1（v1.2）已經把「兩個事件用兩個名字」裁決成契約事實，
 *   這裡的舊宣告與它**直接矛盾** —— 留著它就是留著一份會讓下一個人重現 F-4 的文件。
 *
 * ★ 舊版還有一個後端從來不送的 `reason?`。真正的欄位是 `end_reason`。
 *
 * ⚠️ 客戶端**必須**區分「`winner_seat` 這個 key 不存在」（協定不相容 → 結果未知）
 *   與「存在且為 `null`」（真平手）。型別上兩者都是 `undefined | null`，
 *   分不出來 —— 所以解析一律走 `machine.ts` 的 `readMatchOutcome()`，不要用這個 interface。
 */
export interface MatchEndedPayload {
  /** 對局層級的勝者。`null` = 真平手。**不叫 `winner`。** */
  winner_seat?: Seat | null;
  /** `"normal"` | `"forfeit"` | `"timeout"` | …（後端 `state.public["end_reason"]`）。 */
  end_reason?: string;
  scores?: Record<string, number>;
  returns?: Record<string, number> | null;
  rounds_played?: number;
  decisive_rounds?: number;
  server_seed_commit?: string;
  /** 只有結束後才會出現（C01 §4.2 步驟 5）。裸 hex。 */
  server_seed?: string;
  transcript_hash?: string;
}

/**
 * `match_started` —— 開局廣播。
 *
 * ★★ `seats` 送的是**座位號**（`[0, 1]`），不是座位物件（R-4）。
 *
 * 舊版宣告 `seats?: SeatInfo[]`，那是 **F-4 的同一個形狀，換了一個欄位**：
 * 引擎的 `start_events()` 放進 payload 的是 `state.seats`（一組整數座位號），
 * 而 `SeatInfo[]`（`{seat, display_name, kind, connected}`）是
 * **`GET /matches/{id}` 的 `seats[]`** —— 兩個不同的東西同名。
 * 取樣來源：`src/test/event-fixtures.json` 的三個 `scenarios` ＋ `wire`
 * （後端真實輸出，`tools/capture-event-fixtures.py` 擷取），三份都是 `[0, 1]`。
 *
 * 這份宣告沒有任何執行期程式在用，所以它**不會讓任何測試變紅**
 * （vitest 走 esbuild，只剝型別不檢查型別）。它傷害的是下一個照著它寫 reader 的人：
 * `payload.seats?.[0].display_name` 會拿到 `undefined`，而畫面上就是「沒有對手」。
 * 這正是 §6.1 那條「兩邊各自都對、錯的是接縫」的規則要防的東西。
 *
 * ⚠️ 解析一律走 `machine.ts` 的 `readSeats()` / `readSeatEntry()`：
 *   它兩種形狀都吃，而且**座位號只提供座位號**，其餘欄位沿用已知的那一份
 *   （否則開局那一瞬間對手的暱稱會從真名變成 `Seat 1`）。
 *   型別是文件，不是解析器。
 *
 * 釘住這件事的測試：`src/match/machine.test.ts` 的
 * 「★★ 型別層共犯：api/types.ts 宣告的 seats 必須吃得下線上真的形狀」——
 * 那一支是**編譯期**斷言（`npx tsc --noEmit` / `npm run build` 會紅）。
 */
export interface MatchStartedPayload {
  /** 座位號陣列（`match_started` 真正送的）；物件陣列是 HTTP 投影與 `seat_joined` 的形狀。 */
  seats?: Seat[] | SeatInfo[];
  variant_key?: string;
  rules?: RpsVariantConfig & Record<string, unknown>;
  server_seed_commit?: string;
}

/**
 * `rematch_ready`（C02 §5 v1.2）—— 送到**舊**對局的頻道。
 *
 * 先按再戰的人，他的 HTTP 回應在新對局建立**之前**就結束了
 * （回的是 `waiting_for_opponent` + 舊 id），這個事件是他唯一的得知途徑。
 */
export interface RematchReadyPayload {
  new_match_id?: string;
}
